import CommonApi from '@/pages/api'
import TruncatedAnswer from '@/src/components/HomepageEdit/components/TruncatedElement'
import { formatDate, getYoutubeEmbedUrl } from '@/src/types/helper'
import ContentThumbnail from '@/src/components/ContentThumbnail'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled'
import { useRouter } from 'next/router'
import Button from '@/src/components/button'
import { useStore } from '@/src/types/store'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

// Above this many videos the section scrolls horizontally instead of wrapping
// in a grid, matching the Social posts section (RL-2416).
const GRID_ITEM_LIMIT = 4

interface VideosItem {
  id: number
  is_pinned: boolean
  headline: string
  publish?: string
  display_date: string
  thumbnail: string
  content_url: string
  content_type: string
}

interface VideosSectionProps {
  sectionId: number
  isDraft?: boolean
}

const VideosSection = ({ sectionId, isDraft = false }: VideosSectionProps) => {
  const router = useRouter()
  const { TextColorType } = useStore()
  const [videosData, setVideosData] = useState<any>()
  const [loading, setLoading] = useState<boolean>(false)
  const [playingId, setPlayingId] = useState<number | null>(null)

  const fetchVideosData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        method: 'get',
        path: `show-pinned-content/${sectionId}${isDraft ? '?status=draft' : ''}`
      })
      if (response?.status) {
        setVideosData(response?.data?.pinned_videos)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [sectionId, isDraft])

  useEffect(() => {
    fetchVideosData()
  }, [fetchVideosData])

  const isCarousel = (videosData?.length ?? 0) > GRID_ITEM_LIMIT

  return (
    <div className={`w-full flex justify-center`}>
      {videosData?.length > 0 && (
        <div className="w-full grid grid-cols-1 gap-6">
          <div className="flex justify-between items-center">
            <Typography variant="h5">Videos</Typography>
            <Button
              onClick={() => router.push('/news-insights?media=video')}
              variant="outlined"
              text="More videos"
              extraClass={`rounded-[100px] ${
                TextColorType?.primaryTextDark
                  ? 'text-primary-home border-primary-home'
                  : TextColorType?.secondaryTextDark
                    ? 'text-secondary-home border-secondary-home'
                    : 'text-black border-black'
              }`}
              customStyle={true}
              iconPosition="right"
              icon={<ArrowForwardIcon />}
            />
          </div>
          {loading ? (
            <Box className="w-full flex justify-center items-center h-[300px]">
              <CircularProgress size={25} />
            </Box>
          ) : (
            <div
              className={
                isCarousel
                  ? 'w-full min-w-0 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3'
                  : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              }
            >
              {videosData?.map((item: VideosItem) => (
                <div
                  key={item.id}
                  className={`bg-white shadow-md rounded-[20px] ${
                    isCarousel ? 'shrink-0 snap-start w-[85%] sm:w-[20rem]' : ''
                  }`}
                >
                  <div className="m-3">
                    <div
                      key={item.id}
                      className="relative w-full h-[200px] overflow-hidden rounded-2xl"
                    >
                      {playingId === item.id && getYoutubeEmbedUrl(item.content_url) ? (
                        <iframe
                          src={getYoutubeEmbedUrl(item.content_url, null, true)}
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      ) : (
                        <>
                          <ContentThumbnail
                            item={item}
                            className={`w-full h-full ${
                              item.content_url?.includes('vimeo')
                                ? 'object-contain bg-black'
                                : 'object-cover'
                            }`}
                          />
                          {item.content_type === 'video' && item.content_url && (
                            <PlayCircleFilledIcon
                              onClick={e => {
                                e.stopPropagation()
                                if (getYoutubeEmbedUrl(item.content_url)) {
                                  setPlayingId(item.id) // set this video as playing
                                } else {
                                  window.open(item.content_url, '_blank', 'noopener,noreferrer')
                                }
                              }}
                              className="absolute inset-0 m-auto w-[78px] h-[78px] text-white/80 cursor-pointer hover:scale-110 transition-transform"
                              sx={{
                                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.4))'
                              }}
                            />
                          )}
                        </>
                      )}
                    </div>

                    <div className="pt-2 px-2">
                      <Typography variant="caption" className="mb-2">
                        {formatDate(item?.display_date, 'long')}
                      </Typography>
                    </div>
                    <div className="p-2">
                      <TruncatedAnswer
                        textType="subtitle1"
                        content={item?.headline || ''}
                        maxLength={150}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VideosSection
